import styled from '@emotion/styled';
import React, {
  useEffect,
  useRef,
  useContext,
  useLayoutEffect,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { animated, useSpring } from 'react-spring';
import { Container } from '../../components/container/Container';
import { Close } from '../../components/icons/Close';
import { LayoutContext } from '../../contexts/LayoutContext';

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  color: black;
  padding: 15px 19px;
  cursor: pointer;
`;

const Host = styled(animated.div)`
  width: 100%;
  height: 100%;
  position: fixed;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 990;
  overflow-y: auto;
  background: rgba(244, 247, 247, 1);
  @supports (backdrop-filter: blur(12px)) {
    backdrop-filter: blur(10px);
    background: rgba(244, 247, 247, 0.8);
  }
`;

const ContentContainer = styled(Container)`
  height: 100%;
`;

const Content = styled(animated.div)`
  padding-top: ${(props) => props.theme.headerHeight}px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface ModalProps {
  visible: any;
  onClose?: any;
  children: React.ReactNode;
}

const Dialog: React.FC = ({ children }) => {
  if (typeof window === 'undefined') {
    return null;
  }
  const root = document.getElementById('portal');
  return root ? createPortal(children, root) : null;
};

export const Modal = ({ visible: vis, onClose, children }: ModalProps) => {
  const [visible, setVisible] = useState(vis);
  const { setScrollDisabled } = useContext(LayoutContext);
  const closeRef = useRef(null);
  const { opacity } = useSpring({
    opacity: visible && vis ? 1 : 0,
  });

  const onCloseClick = () => {
    onClose();
  };

  useEffect(() => {
    setScrollDisabled(visible);
  }, [visible]);

  useEffect(() => {
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.keyCode === 27) {
        onClose();
      }
    };

    document.addEventListener('keyup', onKeyUp, false);

    return () => {
      document.removeEventListener('keyup', onKeyUp, false);
    };
  }, []);

  useLayoutEffect(() => {
    setTimeout(() => {
      setVisible(vis);
    }, 175);
  }, [vis]);

  if (!visible) {
    return null;
  }

  return (
    <Dialog>
      <Host
        style={{
          pointerEvents: visible ? 'auto' : 'none',
          opacity,
        }}
      >
        <ContentContainer>
          <Content>{children}</Content>
        </ContentContainer>
        <CloseButton
          ref={closeRef}
          tabIndex={0}
          aria-label="Close Modal"
          onClick={onCloseClick}
        >
          <Close />
        </CloseButton>
      </Host>
    </Dialog>
  );
};
